var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { getFractionAriaText, parseFraction } from '../helper/helper';
var PICKER_OFFSET = 6;
var tileRect = function (p) {
    var _a, _b, _c, _d, _e;
    var scale = (_a = p.scale) !== null && _a !== void 0 ? _a : 1;
    var width = (p.shapeType === 'circle' ? 300 : ((_c = (_b = p.dimensions) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0)) * scale;
    var height = (p.shapeType === 'circle' ? 300 : ((_e = (_d = p.dimensions) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 0)) * scale;
    return { left: p.center.x, top: p.center.y, width: width, height: height };
};
var buildLabel = function (p, t) {
    var parsed = parseFraction(p.fractionSize);
    var fractionText = parsed
        ? getFractionAriaText(parsed.numerator, parsed.denominator || parsed.numerator, p.shapeType)
        : t("text.".concat(p.shapeType), { defaultValue: p.shapeType });
    return t('text.fractionTilePickerLabel', {
        fraction: fractionText,
        defaultValue: fractionText,
    });
};
var buildCandidates = function (manipulative, focusedTile, t) {
    var _a;
    var sourceGroupId = focusedTile.groupId;
    var others = manipulative
        .filter(function (m) {
        if (m.data.type !== 'fractionTiles')
            return false;
        var d = m.data;
        if (d.id === focusedTile.id)
            return false;
        // Exclude tiles that are already in the source's group.
        if (sourceGroupId && d.groupId === sourceGroupId)
            return false;
        return true;
    })
        .map(function (m) { return m.data; });
    var groupMap = new Map();
    var standalones = [];
    for (var _i = 0, others_1 = others; _i < others_1.length; _i++) {
        var p = others_1[_i];
        if (p.groupId) {
            var arr = (_a = groupMap.get(p.groupId)) !== null && _a !== void 0 ? _a : [];
            arr.push(p);
            groupMap.set(p.groupId, arr);
        }
        else {
            standalones.push(p);
        }
    }
    // Stable group numbering matching the canvas order.
    var allGroupIds = [];
    manipulative.forEach(function (m) {
        var _a;
        var gId = (_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId;
        var isFt = m.data.type === 'fractionTiles';
        if (isFt && gId && !allGroupIds.includes(gId))
            allGroupIds.push(gId);
    });
    var candidates = [];
    groupMap.forEach(function (members, groupId) {
        candidates.push({
            kind: 'group',
            groupId: groupId,
            groupNumber: allGroupIds.indexOf(groupId) + 1,
            pieceIds: members.map(function (m) { return m.id; }),
            rects: members.map(function (m) { return tileRect(m); }),
            labels: members.map(function (m) { return buildLabel(m, t); }),
        });
    });
    for (var _b = 0, standalones_1 = standalones; _b < standalones_1.length; _b++) {
        var p = standalones_1[_b];
        var rect = tileRect(p);
        candidates.push(__assign(__assign({ kind: 'standalone', pieceId: p.id }, rect), { label: buildLabel(p, t) }));
    }
    return candidates;
};
var candidateBtnIds = function (c) {
    return c.kind === 'standalone' ? [c.pieceId] : c.pieceIds;
};
var FractionTilesGroupPicker = function () {
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _a = useState(null), focusedPieceId = _a[0], setFocusedPieceId = _a[1];
    var _b = useState(0), activeIdx = _b[0], setActiveIdx = _b[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    useEffect(function () {
        var onOpen = function (data) {
            returnFocusIdRef.current = data.focusedPieceId;
            setFocusedPieceId(data.focusedPieceId);
            setActiveIdx(0);
            announce(t('text.ftGroupPickerOpened', { defaultValue: 'Choose a fraction tile to group with' }));
        };
        customEventHandler.addEventListener('openFractionTilesGroupPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openFractionTilesGroupPicker', onOpen); };
    }, [t]);
    var focusedItem = focusedPieceId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedPieceId; })
        : undefined;
    var focusedPiece = focusedItem && focusedItem.data.type === 'fractionTiles'
        ? focusedItem.data
        : null;
    var candidates = focusedPiece ? buildCandidates(manipulative, focusedPiece, t) : [];
    // Auto-close when source disappears or no candidates remain.
    useEffect(function () {
        if (focusedPieceId && (!focusedPiece || candidates.length === 0)) {
            setFocusedPieceId(null);
        }
    }, [focusedPieceId, focusedPiece, candidates.length]);
    // Click-outside closes; pointerdown is allowed to propagate so canvas drag
    // still works after the picker dismisses.
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedPieceId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedPieceId]);
    // Move focus into the first candidate after the menu's own focus restore
    // settles (~50ms).
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
    }, [focusedPieceId]);
    if (!focusedPieceId || !focusedPiece || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedPieceId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (candidate) {
        var _a;
        // When the source is already grouped, pass its groupId so the merge
        // pulls every member of the source group along with it. The
        // groupDiceFromMenu handler resolves group ids to their members.
        var sourceId = (_a = focusedPiece.groupId) !== null && _a !== void 0 ? _a : focusedPieceId;
        var diceIds = candidate.kind === 'standalone'
            ? [sourceId, candidate.pieceId]
            : [candidate.groupId, sourceId];
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'groupDiceFromMenu',
            panel: 'bottomPanel',
            diceIds: diceIds,
        });
        setFocusedPieceId(null);
    };
    var moveCandidateFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        var cand = candidates[next];
        var targetId = cand.kind === 'standalone' ? cand.pieceId : cand.pieceIds[0];
        requestAnimationFrame(function () {
            var _a;
            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.ftGroupPicker__target[data-btn-id=\"".concat(targetId, "\"]"));
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(1);
        }
        else if (e.key === 'ArrowLeft' ||
            e.key === 'ArrowUp' ||
            (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(-1);
        }
    };
    var getCandidateIdx = function (btnId) {
        return candidates.findIndex(function (c) { return candidateBtnIds(c).includes(btnId); });
    };
    var onBtnFocus = function (btnId) {
        var idx = getCandidateIdx(btnId);
        if (idx !== -1)
            setActiveIdx(idx);
    };
    var btnCounter = 0;
    return (_jsx("div", { ref: containerRef, className: "ftGroupPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.ftGroupPickerLabel', { defaultValue: 'Group fraction tile' }), onKeyDown: onContainerKeyDown, children: candidates.map(function (cand, candIdx) {
            var isActive = candIdx === activeIdx;
            var ids = candidateBtnIds(cand);
            return ids.map(function (pieceId, btnIdx) {
                var isFirstOverall = btnCounter === 0;
                var isFirstInCand = btnIdx === 0;
                btnCounter++;
                var left;
                var top;
                var width;
                var height;
                if (cand.kind === 'standalone') {
                    left = cand.left;
                    top = cand.top;
                    width = cand.width;
                    height = cand.height;
                }
                else {
                    var r = cand.rects[btnIdx];
                    left = r.left;
                    top = r.top;
                    width = r.width;
                    height = r.height;
                }
                var isSrVisible = isFirstInCand;
                var ariaLabel = isSrVisible
                    ? cand.kind === 'standalone'
                        ? t('text.ftGroupPickerStandaloneTarget', {
                            tile: cand.label,
                            defaultValue: "Group with ".concat(cand.label),
                        })
                        : t('text.ftGroupPickerGroupTarget', {
                            number: cand.groupNumber,
                            count: cand.pieceIds.length,
                            tiles: cand.labels.join(', '),
                            defaultValue: "Group with group ".concat(cand.groupNumber, " (").concat(cand.pieceIds.length, " tiles)"),
                        })
                    : undefined;
                return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "ftGroupPicker__target".concat(isActive ? ' ftGroupPicker__target--active' : ''), "data-btn-id": pieceId, "data-cand-idx": candIdx, tabIndex: isSrVisible ? 0 : -1, "aria-hidden": isSrVisible ? undefined : true, style: {
                        position: 'absolute',
                        left: left - PICKER_OFFSET,
                        top: top - PICKER_OFFSET,
                        width: width + PICKER_OFFSET * 2,
                        height: height + PICKER_OFFSET * 2,
                        zIndex: 99999,
                    }, "aria-label": ariaLabel, onFocus: function () { return isSrVisible && onBtnFocus(pieceId); }, onClick: function () { return onSelect(cand); } }, "".concat(pieceId, "-").concat(cand.kind)));
            });
        }) }));
};
export default FractionTilesGroupPicker;
